// src/features/records/utils/attachmentUtils.js

import signedUrlService from '../services/signedUrlService';

/**
 * Get thumbnail URL for an attachment
 * Prioritizes thumbnail paths over main storage path
 * 
 * @param {Object} attachment - Attachment object
 * @param {string} size - Thumbnail size ('400', '800', 'poster')
 * @returns {Promise<string|null>} Signed URL for thumbnail or null
 */
export async function getAttachmentThumbnailUrl(attachment, size = '400') {
  if (!attachment) return null;

  // Check if thumbnail path exists
  const thumbnailPath = attachment.thumbnails?.[size];
  if (thumbnailPath) {
    try {
      return await signedUrlService.getSignedUrl(thumbnailPath);
    } catch (error) {
      console.error('Error getting thumbnail URL:', error);
    }
  }

  // Fallback to main storage path for images
  if (attachment.type === 'image' && attachment.storagePath) {
    try {
      return await signedUrlService.getSignedUrl(attachment.storagePath);
    } catch (error) {
      console.error('Error getting attachment URL:', error);
    }
  }

  return null;
}

/**
 * @typedef {Object} ThumbChoice
 * @property {string|null} primaryPath - Primary thumbnail path to display
 * @property {Array<{path: string, width: number}>} srcSetPaths - Paths for responsive srcSet
 */

/**
 * Deterministically select thumbnail paths for an attachment
 * Centralizes the policy for which thumbnail to use across the application
 * 
 * @param {Object} attachment - Attachment object with type, thumbnails, and storagePath
 * @returns {ThumbChoice} Selected thumbnail paths
 */
export function pickThumbPaths(attachment) {
  const thumbnails = attachment.thumbnails || {};
  const srcSetPaths = [];
  
  // Build srcSet paths for responsive images
  if (thumbnails['400']) {
    srcSetPaths.push({ path: thumbnails['400'], width: 400 });
  }
  if (thumbnails['800']) {
    srcSetPaths.push({ path: thumbnails['800'], width: 800 });
  }

  if (attachment.type === 'video') {
    // For videos: prefer poster > 800 > 400
    const primaryPath = thumbnails.poster || thumbnails['800'] || thumbnails['400'] || null;
    return { primaryPath, srcSetPaths };
  } else {
    // For images: prefer 800 > 400 > original
    const primaryPath = thumbnails['800'] || thumbnails['400'] || attachment.storagePath || null;
    return { primaryPath, srcSetPaths };
  }
}